import { ReactElement, useContext, useEffect, useState } from "react";
import useApiState, { api, IStatusOnlyResponse } from "../../../hooks/use-api-state";
import { ICohortData } from "../../../messages/IGetCohortByIdResponse";
import Loading from "../../common/Loading";
import NotificationsContext from "../../../store/notifications-context";
import styles from "./CohortScheduledTasks.module.css";
import { ICohortScheduledTask, IScheduledTasksResponse } from "../../../messages/IScheduledTasksResponse";

const SEND_EVALFORM_BEFORE_CLASS_END_MINUTES = 30;
const SEND_EVALFORM_REMINDER_AFTER_CLASS_END_WEEKDAYS = 3; //After 3 weekdays

export default function CohortScheduledTasks(props: {
  cohort: ICohortData;
}): ReactElement {
  const notificationsContext = useContext(NotificationsContext);
  const [apiState, setApiState] = useApiState(false);
  const [cohortScheduledTasks, setCohortScheduledTasks] = useState<IScheduledTasksResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function setScheduledTask(task: ICohortScheduledTask, enabled: boolean) {
    api.setScheduledTasksEnabled(
      setApiState,
      props.cohort.id,
      task.task_id,
      enabled)
    .then((status: IStatusOnlyResponse) => {
      if (status.status === "OK") {
        task.schedule.state = enabled ? "ENABLED" : "DISABLED";
      }
    });
  }


  function deleteScheduledTask(task: ICohortScheduledTask) {
    // Ask "Are you sure?" before deleting the task
    if (window.confirm(`Are you sure you want to delete the scheduled task "${task.task_type}"?`)) {
      DeleteTaskList([task.task_id]);
    }
  }


  interface IScheduleTimestamp {
    timezone?: string;
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
  }


  function createEvalFormSendTimeFromCohortData(timezone:string, base_date:number, base_time:string, delta_minutes: number=0): IScheduleTimestamp {
    var sTimeStamp: IScheduleTimestamp = {};
    const time = base_time.split(":");
    //console.log(`base_date=${base_date} timezone=${timezone} time=${time}`);
    const hour = parseInt(time[0]);
    const minute = parseInt(time[1]);
    const timestampUTC = base_date + 60 * (60 * hour + minute + delta_minutes);

    sTimeStamp.timezone = timezone;
    const date = new Date(timestampUTC * 1000);
    //console.log(`date=${date.toDateString()}`);
    sTimeStamp.year = date.getUTCFullYear();
    sTimeStamp.month = date.getUTCMonth() + 1;
    sTimeStamp.day = date.getUTCDate();
    sTimeStamp.hour = date.getUTCHours();
    sTimeStamp.minute = date.getUTCMinutes();
    return sTimeStamp;
  }


  function addWeekdaysToUtcDate(baseDateSecsUtc: number, weekdays: number): number {
    const date = new Date(baseDateSecsUtc * 1000);
    const direction = weekdays >= 0 ? 1 : -1;
    let remaining = Math.abs(weekdays);

    while (remaining > 0) {
      date.setUTCDate(date.getUTCDate() + direction);
      const day = date.getUTCDay(); // 0=Sun, 6=Sat
      if (day !== 0 && day !== 6) {
        remaining--;
      }
    }

    return Math.floor(date.getTime() / 1000);
  }


  function getTasksOfType(taskType: string): string[] {
    let taskList: string[] = [];
    if (cohortScheduledTasks)
      for (let task of cohortScheduledTasks.scheduled_tasks) {
        if (task.task_type === taskType) {
          taskList.push(task.task_id);
        }
      }
    return taskList;
  }


  function DeleteTask(taskId: string) {
    setCohortScheduledTasks((tasks: IScheduledTasksResponse | null) => {
      if (tasks) {
        let new_tasks = { scheduled_tasks: [...tasks.scheduled_tasks] };
        for (let i = 0; i < new_tasks.scheduled_tasks.length; i++) {
          if (new_tasks.scheduled_tasks[i].task_id === taskId) {
            new_tasks.scheduled_tasks.splice(i, 1);
            break;
          }
        }
        return new_tasks;
      }
      return null;
    });
  }


  function DeleteTaskList(taskList: string[]) {
    for (let taskId of taskList) { // Delete existing tasks of the same type
      api.deleteScheduledTask(setApiState, props.cohort.id, taskId).then((status: IStatusOnlyResponse) => {
        if (status.status === "OK") {
          DeleteTask(taskId);
        }
      });
    }
  }

  
  async function createScheduleAt(taskType: string, at: IScheduleTimestamp) {
    const existingTasks = getTasksOfType(taskType);
    let ret = await api.addScheduledTaskAt(setApiState, props.cohort.id, taskType, at.timezone, at.year, at.month, at.day, at.hour, at.minute);
    if (cohortScheduledTasks && ret.status === "OK") {
      setCohortScheduledTasks((tasks: IScheduledTasksResponse | null) => {
        if (tasks) {
          let new_tasks = { scheduled_tasks: [...tasks.scheduled_tasks] };
          new_tasks.scheduled_tasks.push(ret.added_task);
          return new_tasks;
        }
        return null;
      });
      //cohortScheduledTasks.scheduled_tasks.push(status.added_task);
      DeleteTaskList(existingTasks);
    }
    else
    {
      //notificationsContext.addNotification({ message: ret.message ?? "Error occurred when adding the scheduled task." });
      setErrorMessage(ret.message ?? "Error occurred when adding the scheduled task.");
    }
  }


  function createEvalformSchedule() {
    const evalFormAt = createEvalFormSendTimeFromCohortData(props.cohort.timezone, props.cohort.enddate_secs_utc, props.cohort.end_time, -SEND_EVALFORM_BEFORE_CLASS_END_MINUTES);
    createScheduleAt("evalform-email", evalFormAt);
    let reminderDate = addWeekdaysToUtcDate(props.cohort.enddate_secs_utc, SEND_EVALFORM_REMINDER_AFTER_CLASS_END_WEEKDAYS); // Add working days to the end date
    const evalFormReminderAt = createEvalFormSendTimeFromCohortData(props.cohort.timezone, reminderDate, props.cohort.start_time);
    createScheduleAt("evalform-reminder-email", evalFormReminderAt);
    //setCohortScheduledTasks(cohortScheduledTasks);
  }


  async function createElearningReminderSchedule() {
    //At 10:00 AM, on day 1 and 15 of the month: cron("0 10 1,15 * *")
    let taskType = "elearning-regular-reminder-email";
    const existingTasks = getTasksOfType(taskType);
    let status = await api.addScheduledTaskCron(setApiState, props.cohort.id, taskType,
      "CET", "0", "10", "1,15", "*", "?", "*");
    if (cohortScheduledTasks && status.status === "OK") {
      cohortScheduledTasks.scheduled_tasks.push(status.added_task);
      DeleteTaskList(existingTasks);
    }
  }


  function getTaskName(taskType: String): String {
    switch (taskType) {
      case "evalform-email":
        return "Eval form";
      case "evalform-reminder-email":
        return "Eval form reminder";
      case "elearning-regular-reminder-email":
        return "E-learning reminder";
      default:
        return taskType;
    }
  }

  useEffect(() => {
    api.getCohortScheduledTasksOfCohort(
      setApiState,
      props.cohort.id)
      .then((scheduled_tasks: IScheduledTasksResponse) => {
        setCohortScheduledTasks(scheduled_tasks);
      });
  }, [setApiState, props.cohort.id]);

  function copyToClipboard(value:string|null|undefined, message:string) {
    if (value) {
      navigator.clipboard.writeText(value);
      if (message) {
        notificationsContext.addNotification({ message: message });
      }
    }
  }

  return (
    <>
      <Loading isLoading={apiState.isLoading}>
        <div className="cy__form form__evaluations">
          {
            !!cohortScheduledTasks && cohortScheduledTasks.scheduled_tasks.length > 0 ?
              <>
                {cohortScheduledTasks.scheduled_tasks.map((task: ICohortScheduledTask) => {
                  return (
                    <div key={task.task_id} className={styles.task_list_element}>
                      {task.schedule.state === "ENABLED" ?
                        <img
                          src="/icons/OK_40x40_2.png"
                          className={styles.icon}
                          title="The task is enabled."
                          alt=""
                        />
                        :
                        <img
                          src="/icons/error_40x40.png"
                          className={styles.icon}
                          title="The task is disabled."
                          alt=""
                        />
                      }
                      <p className={task.schedule.state === "ENABLED" ? styles.enabled : styles.disabled}>
                        {task.schedule.schedule_expression} {task.schedule.schedule_timezone}&nbsp;&nbsp;&nbsp;<b>{getTaskName(task.task_type)}</b>
                      </p>
                      <div className={styles.copyTaskIdDiv}
                        //data-for={`member-tooltip-${props.cohortId}`}
                        //data-tip="Copy cohort ID to clipboard."
                        onClick={() => {
                          copyToClipboard(task.task_id,`The UUID of the task has been copied to the clipboard.`);
                        }}>
                        <img
                          src="/icons/copy.svg"
                          className={`${styles.membericon} ${styles.member_clipboard_icon}`}
                          alt=""
                        />
                        <div className={styles.overlay_text}>ID</div>
                      </div>
                      {task.task_payload && 
                        <p>
                          {task.task_payload.send_summary_email_to &&
                            <span className={styles.summary_email}><b>Summary to:</b> {task.task_payload.send_summary_email_to}</span>
                          }
                          {task.task_payload.debug_email &&
                            <span className={styles.debug_email}><b>Debug to:</b> {task.task_payload.debug_email}</span>
                          }
                        </p>
                      }
                      <button
                        className={`btn primary ${styles.deleteButton}`}
                        onClick={() => deleteScheduledTask(task)}>
                        Delete
                      </button>
                      {task.schedule.state === "ENABLED" ?
                        <button
                          className={`btn primary ${styles.enabledisableButton}`}
                          onClick={() => setScheduledTask(task, false)}>
                          Disable
                        </button>
                        :
                        <button
                          className={`btn secondary ${styles.enabledisableButton}`}
                          onClick={() => setScheduledTask(task, true)}>
                          Enable
                        </button>
                      }
                    </div>
                  );
                })}
              </>
              :
              <p>There are no scheduled tasks for this cohort</p>
          }
        </div>

        {errorMessage &&
          <div className={styles.errorMessage}>
            {errorMessage}
          </div>
        }

        {!props.cohort.elearning &&
          <div>
            <button className={`btn primary wide ${styles.createNew}`} onClick={createEvalformSchedule} >
              Create eval form schedule
            </button>
          </div>
        }

        {props.cohort.elearning &&
          <div>
            <button className={`btn primary wide ${styles.createNew}`} onClick={createElearningReminderSchedule} >
              Create E-learning reminder schedule
            </button>
          </div>
        }

      </Loading>
    </>
  );
}
